import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';

const NAVY = '#0C2D48';
const DANGER = '#A12710';
const ALLOWED_ROLES = ['super_admin', 'cs_manager', 'cs_agent'];

const BUSINESS_PLAN_PILLS = [
    { label: 'None',     value: '' },
    { label: 'Basic',    value: 'Basics' },
    { label: 'Pro',      value: 'Pro' },
    { label: 'Team Pro', value: 'Team' },
];
const PERSONAL_PLAN_PILLS = [
    { label: 'None',     value: 'none' },
    { label: 'Personal', value: 'Personal' },
    { label: 'Family',   value: 'Family' },
];

const box = {
    background: '#fff',
    border: '1px solid #e8e8e8',
    borderRadius: 12,
    padding: '1rem',
};

const inputBase = {
    width: '100%',
    border: '1px solid #d6dce3',
    borderRadius: 8,
    padding: '0.55rem 0.7rem',
    boxSizing: 'border-box',
    fontFamily: 'Raleway, sans-serif',
    fontSize: '0.9rem',
};

const labelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#56606a',
    marginBottom: '0.25rem',
};

const btnBase = {
    border: 'none',
    borderRadius: 8,
    padding: '0.48rem 0.85rem',
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: 'Raleway, sans-serif',
    fontSize: '0.85rem',
};

const thStyle = {
    textAlign: 'left',
    padding: '0.5rem 0.75rem',
    fontSize: '0.74rem',
    fontWeight: 800,
    color: '#56606a',
    whiteSpace: 'nowrap',
};

const tdStyle = {
    padding: '0.6rem 0.75rem',
    verticalAlign: 'middle',
};

// ACCOUNT-3: the account's real commercial terms, from its active programme
// (falling back to subscription status). Replaces the inert trialEndsAt column.
function programmeTerms(u) {
    const p = u.programme;
    if (p) {
        const ends = p.endsAt ? new Date(p.endsAt).toLocaleDateString() : null;
        switch (p.type) {
            case 'staff': return 'Staff';
            case 'reviewer': return 'Reviewer';
            case 'comp_open': return 'Comp — no expiry';
            case 'comp_fixed': return `Comp until ${ends || '?'}`;
            case 'trial': return `Trial ends ${ends || '?'}`;
            case 'discount': return `${p.discountPercent ?? 0}% discount`;
            default: return p.type;
        }
    }
    const st = String(u.subscriptionStatus || '').toLowerCase();
    if (st === 'active') return 'Paid · active';
    if (st === 'trialing') return 'Stripe trial';
    if (st === 'past_due') return 'Payment due';
    if (st === 'cancelled') return 'Cancelled';
    if (st === 'unpaid') return 'Unpaid';
    return '—';
}

function friendlyDate(value) {
    if (!value) return '—';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString();
}

function rolePill(role) {
    const map = {
        super_admin: { bg: '#f3eeff', text: '#6b21a8', label: 'Super Admin' },
        cs_manager: { bg: '#eff6ff', text: '#1d4ed8', label: 'CS Manager' },
        admin: { bg: '#eff6ff', text: '#1d4ed8', label: 'Admin' },
        cs_agent: { bg: '#eefdf3', text: '#166534', label: 'CS Agent' },
        member: { bg: '#f8fafc', text: '#475569', label: 'Member' },
    };
    const c = map[String(role || '').toLowerCase()] || { bg: '#f3f4f6', text: '#334155', label: role || 'Unknown' };
    return <span style={{ background: c.bg, color: c.text, borderRadius: 999, padding: '0.2rem 0.55rem', fontSize: '0.72rem', fontWeight: 800 }}>{c.label}</span>;
}

function planPill(plan) {
    const map = {
        enterprise: { bg: '#fdf4ff', text: '#7e22ce' },
        team:       { bg: '#eff6ff', text: '#1d4ed8' },
        pro:        { bg: '#ecfdf5', text: '#065f46' },
        basics:     { bg: '#f0fdf4', text: '#15803d' },
        trial:      { bg: '#fff7ed', text: '#9a3412' },
        personal:   { bg: '#fef9c3', text: '#854d0e' },
        family:     { bg: '#fce7f3', text: '#9d174d' },
    };
    const label = plan || 'Trial';
    // effectivePlan can be compound e.g. "Pro + Personal" — derive key from first segment
    const key = String(label).split('+')[0].trim().toLowerCase();
    const c = map[key] || { bg: '#f3f4f6', text: '#334155' };
    return <span style={{ background: c.bg, color: c.text, borderRadius: 999, padding: '0.2rem 0.55rem', fontSize: '0.72rem', fontWeight: 800 }}>{label}</span>;
}

function statusPill(status) {
    const active = String(status || '').toLowerCase() === 'active';
    return (
        <span style={{ background: active ? '#dcfce7' : '#fee2e2', color: active ? '#166534' : '#991b1b', borderRadius: 999, padding: '0.2rem 0.55rem', fontSize: '0.72rem', fontWeight: 800 }}>
            {status || 'unknown'}
        </span>
    );
}

function userTypePill(userType) {
    const map = {
        influencer: { bg: '#fdf4ff', text: '#a21caf', label: 'Influencer' },
        friends_family: { bg: '#fff1f2', text: '#be123c', label: 'F&F' },
        pro_team_owner: { bg: '#eff6ff', text: '#1d4ed8', label: 'Team Owner' },
        pro_team_member: { bg: '#f0f9ff', text: '#0369a1', label: 'Team Member' },
        pro_user: { bg: '#ecfdf5', text: '#065f46', label: 'Pro' },
        basic_user: { bg: '#f8fafc', text: '#475569', label: 'Basic' },
    };
    const c = map[String(userType || '').toLowerCase()] || { bg: '#f3f4f6', text: '#334155', label: userType || '—' };
    return <span style={{ background: c.bg, color: c.text, borderRadius: 999, padding: '0.2rem 0.55rem', fontSize: '0.72rem', fontWeight: 800 }}>{c.label}</span>;
}

function logStatusPill(status) {
    const map = {
        completed: { bg: '#ecfdf5', text: '#166534' },
        pending: { bg: '#fff7ed', text: '#9a3412' },
        approved: { bg: '#eff6ff', text: '#1d4ed8' },
        rejected: { bg: '#fee2e2', text: '#991b1b' },
    };
    const c = map[String(status || '').toLowerCase()] || { bg: '#f3f4f6', text: '#334155' };
    return <span style={{ background: c.bg, color: c.text, borderRadius: 999, padding: '0.18rem 0.5rem', fontSize: '0.72rem', fontWeight: 800 }}>{status}</span>;
}

function actionLabel(action) {
    const map = {
        account_change: 'Account Change',
        trial_extension_request: 'Trial Ext. Request',
        trial_extension_approved: 'Trial Ext. Approved',
        trial_extension_rejected: 'Trial Ext. Rejected',
    };
    return map[action] || action;
}

function csvEscape(value) {
    return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function downloadCsv(filename, headers, rows) {
    const csv = [headers.map(csvEscape).join(','), ...rows.map((r) => r.map(csvEscape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

async function parseApiError(res, fallback) {
    try { const d = await res.json(); return d?.error || fallback; } catch { return fallback; }
}

// ── SEAT-5: break-glass ownership transfer panel (super_admin) ────────────────
// Stripe cannot move a subscription between customers, so a transfer is cancel-and-
// recreate on live billing. This runs the sequenced engine and shows the TransferJob
// state so a stuck transfer is visible. States:
//   pending / awaiting_payment (new owner must add a card) / flipped (ownership moved,
//   old sub cancel may be pending) / completed / failed / aborted.
const TRANSFER_STATE_HELP = {
    pending: 'Prechecks passed, creating the new subscription.',
    awaiting_payment: 'New subscription created but not active yet. The new owner must add a card. Resume once they have paid.',
    flipped: 'Ownership has moved and the new owner is active. The old subscription cancel is pending. Resume to retry the cancel.',
    completed: 'Transfer complete. Old subscription cancelled.',
    failed: 'An error occurred. See the message. Resume is safe to retry.',
    aborted: 'Cancelled before the flip. Nothing changed.',
};

function OwnershipTransferPanel() {
    const [businessId, setBusinessId] = useState('');
    const [newOwnerId, setNewOwnerId] = useState('');
    const [precheck, setPrecheck] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    async function loadJobs() {
        try {
            const r = await api('/api/admin/business-transfer');
            if (r?.ok) { const d = await r.json(); setJobs(d.jobs || []); }
        } catch (_) {}
    }
    useEffect(() => { loadJobs(); }, []);

    async function doPrecheck() {
        setMsg(''); setPrecheck(null);
        try {
            const r = await api('/api/admin/business-transfer/precheck', { method: 'POST', body: JSON.stringify({ businessId, newOwnerId }) });
            setPrecheck(await r.json());
        } catch (err) { setMsg(err.message || 'Precheck failed'); }
    }

    async function doStart() {
        if (!window.confirm('Start a live ownership transfer? This creates a new Stripe subscription on the new owner and cancels the old one.')) return;
        setBusy(true); setMsg('');
        try {
            const r = await api('/api/admin/business-transfer', { method: 'POST', body: JSON.stringify({ businessId, newOwnerId }) });
            const d = await r.json();
            setMsg(r.ok ? `Transfer ${d.job?.state || 'started'}${d.awaitingPayment ? ' — new owner must add a card' : ''}` : `Refused: ${d.reason || d.error}${d.detail ? ' — ' + d.detail : ''}`);
            await loadJobs();
        } catch (err) { setMsg(err.message || 'Transfer failed'); }
        finally { setBusy(false); }
    }

    async function act(jobId, action) {
        setBusy(true); setMsg('');
        try {
            const r = await api(`/api/admin/business-transfer/${jobId}/${action}`, { method: 'POST' });
            const d = await r.json();
            setMsg(r.ok ? `Job ${d.job?.state || action}` : `Failed: ${d.reason || d.error}`);
            await loadJobs();
        } catch (err) { setMsg(err.message || `${action} failed`); }
        finally { setBusy(false); }
    }

    const inp = { padding: '0.5rem 0.6rem', borderRadius: 8, border: '1px solid #d7dde5', fontFamily: 'Raleway, sans-serif', fontSize: '0.85rem', minWidth: 280 };

    return (
        <div style={box}>
            <div style={{ fontWeight: 800, color: NAVY, marginBottom: '0.4rem' }}>Ownership transfer (break-glass)</div>
            <p style={{ fontSize: '0.82rem', color: '#667085', margin: '0 0 1rem', lineHeight: 1.6 }}>
                Supervised transfer of a business to a new owner. Stripe cannot move a subscription between customers,
                so this cancels the old subscription and creates a new one on the new owner. Run the precheck first.
                It refuses unless the current owner is active or trialing, the owner has no household with dependent
                logins, the target is a member, and no transfer is already pending.
            </p>

            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <input style={inp} placeholder="Business ID (UUID)" value={businessId} onChange={(e) => setBusinessId(e.target.value)} />
                <input style={inp} placeholder="New owner user ID (a member)" value={newOwnerId} onChange={(e) => setNewOwnerId(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <button onClick={doPrecheck} disabled={!businessId || !newOwnerId} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #d7dde5', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>Precheck</button>
                <button onClick={doStart} disabled={busy || !precheck?.ok} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: 'none', background: precheck?.ok ? NAVY : '#ccc', color: '#fff', cursor: precheck?.ok ? 'pointer' : 'not-allowed', fontWeight: 700 }}>Start transfer</button>
            </div>

            {precheck && (
                <div style={{ padding: '0.6rem 0.8rem', borderRadius: 8, marginBottom: '0.75rem', background: precheck.ok ? '#eafbe7' : '#fdeaea', color: precheck.ok ? '#256029' : '#9a3412', fontSize: '0.84rem' }}>
                    {precheck.ok ? 'Precheck passed — safe to transfer.' : `Refused: ${precheck.reason}${precheck.detail ? ' — ' + precheck.detail : ''}`}
                </div>
            )}
            {msg && <div style={{ fontSize: '0.84rem', color: '#334155', marginBottom: '0.75rem' }}>{msg}</div>}

            <div style={{ fontWeight: 700, color: NAVY, margin: '0.5rem 0' }}>Transfer jobs</div>
            {jobs.length === 0 && <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No transfer jobs.</div>}
            <div style={{ display: 'grid', gap: '0.5rem' }}>
                {jobs.map((j) => (
                    <div key={j.id} style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.82rem' }}>
                        <div><strong>{j.state}</strong> — business {j.businessId} — {j.oldOwnerId} → {j.newOwnerId}</div>
                        <div style={{ color: '#667085', margin: '0.25rem 0' }}>{TRANSFER_STATE_HELP[j.state] || ''}</div>
                        {j.error && <div style={{ color: '#9a3412' }}>Error: {j.error}</div>}
                        {['awaiting_payment', 'flipped', 'failed'].includes(j.state) && (
                            <button onClick={() => act(j.id, 'resume')} disabled={busy} style={{ marginRight: '0.5rem', marginTop: '0.35rem', padding: '0.35rem 0.8rem', borderRadius: 6, border: '1px solid #d7dde5', background: '#fff', cursor: 'pointer' }}>Resume</button>
                        )}
                        {['pending', 'awaiting_payment'].includes(j.state) && (
                            <button onClick={() => act(j.id, 'abort')} disabled={busy} style={{ marginTop: '0.35rem', padding: '0.35rem 0.8rem', borderRadius: 6, border: '1px solid #d7dde5', background: '#fff', cursor: 'pointer', color: '#9a3412' }}>Abort</button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AdminPanel() {
    const { currentUser } = useCurrentUser();
    const role = String(currentUser?.role || '').toLowerCase();

    if (!ALLOWED_ROLES.includes(role)) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'Raleway, sans-serif' }}>
                <h2 style={{ color: DANGER }}>Access Denied</h2>
                <p style={{ color: '#667085' }}>This panel requires Super Admin, CS Manager, or CS Agent access.</p>
            </div>
        );
    }

    return <AdminPanelInner currentUser={currentUser} role={role} />;
}

function AdminPanelInner({ currentUser, role }) {
    const [tab, setTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [metadata, setMetadata] = useState({ plans: [], editablePlans: [], accountStatuses: [], customerProgramTypes: [], createUserRoles: [], programmeTypes: [], csManagerGrantable: [] });
    // Programme types this admin may grant: super_admin → all; cs_manager → trial + comp_fixed.
    const grantableProgrammes = role === 'super_admin'
        ? (metadata.programmeTypes || [])
        : (metadata.csManagerGrantable || []);
    const [planFilter, setPlanFilter] = useState('');
    const [globalLogs, setGlobalLogs] = useState([]);
    const [requests, setRequests] = useState([]);

    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingGlobalLogs, setLoadingGlobalLogs] = useState(false);
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [loadingUserLogs, setLoadingUserLogs] = useState(false);
    const [saving, setSaving] = useState(false);

    const [queryText, setQueryText] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resetFallbackUrl, setResetFallbackUrl] = useState('');

    const [selectedUserId, setSelectedUserId] = useState(null);
    const [userDetailLogs, setUserDetailLogs] = useState([]);

    const [accountForm, setAccountForm] = useState({
        role: '', plan: '', status: '', trialDays: '', reason: '',
        // ACCOUNT-3 account programme (accessTier + legacy programType/discount retired from the form)
        programmeType: 'none', programmeEndsAt: '', programmeDiscountPercent: '',
    });

    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addUserForm, setAddUserForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'member',
        plan: 'Trial',
        status: 'active',
        trialDays: '',
        reason: '',
        programmeType: 'none', programmeEndsAt: '', programmeDiscountPercent: '',
    });
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [newUserCredentials, setNewUserCredentials] = useState(null);

    // Map of requestId -> { mode: 'approve'|'reject', approvedDays: '', reason: '' }
    const [reviewState, setReviewState] = useState({});

    const selectedUser = useMemo(
        () => users.find((u) => u.id === selectedUserId) || null,
        [users, selectedUserId]
    );

    const filteredUsers = useMemo(() => {
        const q = queryText.trim().toLowerCase();
        let result = users;
        if (q) {
            result = result.filter((u) =>
                [u.name, u.email, u.role, u.effectivePlan, u.plan, u.status, u.userType]
                    .some((v) => String(v || '').toLowerCase().includes(q))
            );
        }
        if (planFilter) {
            result = result.filter((u) =>
                String(u.effectivePlan || u.plan || '').toLowerCase().includes(planFilter.toLowerCase())
            );
        }
        return result;
    }, [users, queryText, planFilter]);

    async function loadUsers() {
        setLoadingUsers(true);
        setError('');
        try {
            const [uRes, mRes] = await Promise.all([
                api('/api/users'),
                api('/api/aidey-team/metadata'),
            ]);
            if (!uRes.ok) throw new Error(await parseApiError(uRes, 'Failed to load users'));
            if (!mRes.ok) throw new Error(await parseApiError(mRes, 'Failed to load metadata'));
            const [uData, mData] = await Promise.all([uRes.json(), mRes.json()]);
            setUsers(Array.isArray(uData.users) ? uData.users : []);
            setMetadata({
                plans:              Array.isArray(mData.plans)              ? mData.plans              : [],
                editablePlans:      Array.isArray(mData.editablePlans)      ? mData.editablePlans      : [],
                accountStatuses:    Array.isArray(mData.accountStatuses)    ? mData.accountStatuses    : [],
                customerProgramTypes: Array.isArray(mData.customerProgramTypes) ? mData.customerProgramTypes : [],
                createUserRoles:    Array.isArray(mData.createUserRoles)    ? mData.createUserRoles    : [],
                programmeTypes:     Array.isArray(mData.programmeTypes)     ? mData.programmeTypes     : [],
                csManagerGrantable: Array.isArray(mData.csManagerGrantable) ? mData.csManagerGrantable : [],
            });
        } catch (e) {
            setError(e.message || 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    }

    async function loadGlobalLogs() {
        setLoadingGlobalLogs(true);
        try {
            const res = await api('/api/aidey-team/work-log?limit=200');
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load work log'));
            const data = await res.json();
            setGlobalLogs(Array.isArray(data.logs) ? data.logs : []);
        } catch (e) {
            setError(e.message || 'Failed to load work log');
        } finally {
            setLoadingGlobalLogs(false);
        }
    }

    async function loadRequests() {
        setLoadingRequests(true);
        try {
            const res = await api('/api/aidey-team/work-requests?status=pending');
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load requests'));
            const data = await res.json();
            setRequests(Array.isArray(data.requests) ? data.requests : []);
        } catch (e) {
            setError(e.message || 'Failed to load requests');
        } finally {
            setLoadingRequests(false);
        }
    }

    async function loadUserLogs(userId) {
        if (!userId) { setUserDetailLogs([]); return; }
        setLoadingUserLogs(true);
        try {
            const res = await api(`/api/aidey-team/work-log/${userId}?limit=500`);
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load audit history'));
            const data = await res.json();
            setUserDetailLogs(Array.isArray(data.logs) ? data.logs : []);
        } catch (e) {
            setError(e.message || 'Failed to load audit history');
        } finally {
            setLoadingUserLogs(false);
        }
    }

    useEffect(() => { loadUsers(); loadRequests(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (tab === 'worklog' && globalLogs.length === 0) loadGlobalLogs(); // eslint-disable-line react-hooks/exhaustive-deps
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectedUser) { setUserDetailLogs([]); return; }
        const prog = selectedUser.programme || null;
        setAccountForm({
            role: selectedUser.role || 'member',
            plan: selectedUser.plan || '',
            status: selectedUser.status || 'active',
            trialDays: '',
            personalTier: selectedUser.personalTier || 'none',
            reason: '',
            // ACCOUNT-3 programme (source of truth is the active AccountProgramme row)
            programmeType: prog?.type || 'none',
            programmeEndsAt: prog?.endsAt ? String(prog.endsAt).slice(0, 10) : '',
            programmeDiscountPercent: Number.isFinite(prog?.discountPercent) ? String(prog.discountPercent) : '',
        });
        loadUserLogs(selectedUser.id); // eslint-disable-line react-hooks/exhaustive-deps
    }, [selectedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

    function openUserDetail(userId) {
        if (!userId) return;
        setSelectedUserId(userId);
        setTab('users');
        setError('');
        setSuccess('');
    }

    function closeUserDetail() {
        setSelectedUserId(null);
        setUserDetailLogs([]);
        setError('');
        setSuccess('');
    }

    async function submitAccountChange(e) {
        e.preventDefault();
        if (!selectedUserId) return;
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = { status: accountForm.status };
            if (role === 'super_admin') {
                payload.role = accountForm.role;
            }
            if (accountForm.plan !== (selectedUser.plan || '')) payload.plan = accountForm.plan;
            if (accountForm.personalTier !== (selectedUser.personalTier || 'none')) payload.personalTier = accountForm.personalTier;
            if (accountForm.reason) payload.reason = accountForm.reason;
            if (accountForm.trialDays !== '') payload.trialDays = Number(accountForm.trialDays);
            // ACCOUNT-3 programme (only sent when changed from the loaded value)
            const loadedProg = selectedUser.programme?.type || 'none';
            if (accountForm.programmeType !== loadedProg
                || accountForm.programmeEndsAt !== (selectedUser.programme?.endsAt ? String(selectedUser.programme.endsAt).slice(0, 10) : '')) {
                payload.programmeType = accountForm.programmeType;
                if (accountForm.programmeEndsAt) payload.programmeEndsAt = new Date(accountForm.programmeEndsAt).toISOString();
                if (accountForm.programmeDiscountPercent !== '') payload.programmeDiscountPercent = Number(accountForm.programmeDiscountPercent);
            }

            const res = await api(`/api/aidey-team/users/${selectedUserId}/account`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to save account changes'));
            setSuccess('Account update saved and logged.');
            await Promise.all([loadUsers(), loadUserLogs(selectedUserId)]);
            setAccountForm((p) => ({ ...p, reason: '', trialDays: '' }));
        } catch (e2) {
            setError(e2.message || 'Failed to save account changes');
        } finally {
            setSaving(false);
        }
    }

    function openAddUserModal() {
        setAddUserForm({
            name: '',
            email: '',
            password: '',
            role: 'member',
            plan: 'Trial',
            personalTier: 'none',
            status: 'active',
            trialDays: '',
            reason: '',
            programmeType: 'none', programmeEndsAt: '', programmeDiscountPercent: '',
        });
        setNewUserCredentials(null);
        setError('');
        setSuccess('');
        setShowAddUserModal(true);
    }

    function closeAddUserModal() {
        if (addUserLoading) return;
        setShowAddUserModal(false);
    }

    async function submitAddUser(e) {
        e.preventDefault();
        if (role !== 'super_admin') {
            setError('Only Super Admins can add users.');
            return;
        }

        setAddUserLoading(true);
        setError('');
        setSuccess('');
        setNewUserCredentials(null);

        try {
            const payload = {
                name: addUserForm.name.trim(),
                email: addUserForm.email.trim(),
                role: addUserForm.role,
                plan: addUserForm.plan,
                personalTier: addUserForm.personalTier,
                status: addUserForm.status,
            };
            if (addUserForm.password.trim()) payload.password = addUserForm.password;
            if (addUserForm.trialDays !== '') payload.trialDays = Number(addUserForm.trialDays);
            if (addUserForm.reason.trim()) payload.reason = addUserForm.reason.trim();
            // ACCOUNT-3 programme grant at creation
            if (addUserForm.programmeType && addUserForm.programmeType !== 'none') {
                payload.programmeType = addUserForm.programmeType;
                if (addUserForm.programmeEndsAt) payload.programmeEndsAt = new Date(addUserForm.programmeEndsAt).toISOString();
                if (addUserForm.programmeDiscountPercent !== '') payload.programmeDiscountPercent = Number(addUserForm.programmeDiscountPercent);
            }

            const res = await api('/api/aidey-team/users', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to create user'));
            const data = await res.json();

            setSuccess(`User ${data?.user?.email || addUserForm.email} created successfully.`);
            if (data?.temporaryPasswordGenerated && data?.temporaryPassword) {
                setNewUserCredentials({
                    email: data.user.email,
                    temporaryPassword: data.temporaryPassword,
                });
            }

            await loadUsers();
            if (data?.user?.id) setSelectedUserId(data.user.id);
            setShowAddUserModal(false);
        } catch (err) {
            setError(err.message || 'Failed to create user');
        } finally {
            setAddUserLoading(false);
        }
    }

    async function removeUser(userId) {
        if (!userId) return;
        const target = users.find((u) => u.id === userId);
        const displayName = target?.name || target?.email || 'this user';
        // eslint-disable-next-line no-restricted-globals
        const reason = window.prompt(`Remove ${displayName}? This is permanent and cannot be undone.\n\nEnter a reason (required):`);
        if (reason === null) return; // cancelled
        if (!reason.trim()) { setError('A reason is required to remove a user.'); return; }
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await api(`/api/aidey-team/users/${userId}`, {
                method: 'DELETE',
                body: JSON.stringify({ reason: reason.trim() }),
            });
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to remove user'));
            setSuccess(`User ${displayName} has been permanently removed.`);
            closeUserDetail();
            await loadUsers();
        } catch (e) {
            setError(e.message || 'Failed to remove user');
        } finally {
            setSaving(false);
        }
    }

    async function sendResetLink(userId) {
        if (!userId) return;
        setSaving(true);
        setError('');
        setSuccess('');
        setResetFallbackUrl('');
        try {
            const res = await api(`/api/aidey-team/users/${userId}/send-password-reset`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await parseApiError(res, 'Failed to send password reset email'));
            const data = await res.json().catch(() => ({}));
            if (data?.emailSent === false) {
                if (data?.fallbackResetUrl) setResetFallbackUrl(data.fallbackResetUrl);
                if (data?.reason === 'suppressed') {
                    const since = data?.suppressedSince ? new Date(data.suppressedSince).toLocaleDateString() : null;
                    const reason = String(data?.suppressionReason || 'BOUNCE').toLowerCase();
                    setError(`Email couldn't be delivered — this address is on the SES suppression list (${reason}${since ? ` since ${since}` : ''}), so it bounced previously and mail is being dropped. The mailbox may not exist. Use the fallback link below, or fix the mailbox and clear the suppression entry first.`);
                } else if (data?.emailErrorCode === 'ses_recipient_not_verified') {
                    setError('Email could not be delivered because SES rejected the recipient as unverified. Use the fallback link below, or verify the recipient in SES.');
                } else {
                    setError(data?.emailErrorMessage || 'Password reset link generated, but email delivery failed. Use the fallback link below.');
                }
                return;
            }
            setSuccess('Password reset email sent.');
        } catch (e2) {
            setError(e2.message || 'Failed to send password reset email');
        } finally {
            setSaving(false);
        }
    }

    function startReview(id, mode) {
        setReviewState((prev) => ({ ...prev, [id]: { mode, approvedDays: '', reason: '' } }));
    }

    function cancelReview(id) {
        setReviewState((prev) => { const n = { ...prev }; delete n[id]; return n; });
    }

    async function confirmReview(requestId) {
        const rv = reviewState[requestId];
        if (!rv) return;
        if (rv.mode === 'approve') {
            const days = Number(rv.approvedDays);
            if (!Number.isFinite(days) || days < 1) { setError('Approved days must be at least 1.'); return; }
        }
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = { decision: rv.mode };
            if (rv.mode === 'approve') payload.approvedDays = Number(rv.approvedDays);
            if (rv.reason) payload.reason = rv.reason;

            const res = await api(`/api/aidey-team/work-requests/${requestId}/review`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await parseApiError(res, `Failed to ${rv.mode} request`));
            setSuccess(`Request ${rv.mode}d successfully.`);
            cancelReview(requestId);
            await Promise.all([loadRequests(), loadUsers()]);
        } catch (e2) {
            setError(e2.message || `Failed to ${rv.mode} request`);
        } finally {
            setSaving(false);
        }
    }

    function exportUserLogsCsv() {
        if (!selectedUser || userDetailLogs.length === 0) return;
        const headers = ['Log ID', 'Created At', 'Action', 'Status', 'Actor Name', 'Actor Email', 'Approver Name', 'Approver Email', 'Reason', 'Metadata'];
        const rows = userDetailLogs.map((log) => [
            log.id, log.createdAt, log.action, log.status,
            log.actorUser?.name || '', log.actorUser?.email || '',
            log.approvedByUser?.name || '', log.approvedByUser?.email || '',
            log.reason || '', JSON.stringify(log.metadata || {}),
        ]);
        const safe = String(selectedUser.email || selectedUser.name || 'user').replace(/[^a-zA-Z0-9._-]/g, '_');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        downloadCsv(`aidey-audit-${safe}-${stamp}.csv`, headers, rows);
    }

    const pendingCount = requests.length;
    const plans = metadata.plans.length ? metadata.plans : ['Trial', 'Basics', 'Pro', 'Team', 'Enterprise', 'Personal', 'Family'];
    const editablePlans = metadata.editablePlans.length ? metadata.editablePlans : ['Trial', 'Basics', 'Pro', 'Team', 'Enterprise'];
    const statuses = metadata.accountStatuses.length ? metadata.accountStatuses : ['active', 'suspended', 'closed'];
    const createUserRoles = Array.isArray(metadata.createUserRoles) && metadata.createUserRoles.length
        ? metadata.createUserRoles
        : ['member', 'admin', 'super_admin', 'cs_manager', 'cs_agent'];

    return (
        <div style={{ padding: '1rem', maxWidth: 1280, margin: '0 auto', fontFamily: 'Raleway, sans-serif' }}>

            {/* Header */}
            <div style={{ ...box, background: NAVY, color: '#fff', border: 'none', marginBottom: '1rem' }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>Admin Panel</div>
                <div style={{ opacity: 0.85, fontSize: '0.84rem', marginTop: '0.2rem' }}>
                    {currentUser?.name || 'Current user'} · {role.replace('_', ' ')}
                </div>
            </div>

            {error && (
                <div style={{ ...box, background: '#fff1f2', borderColor: '#fecdd3', color: DANGER, marginBottom: '0.8rem' }}>
                    ⚠ {error}
                </div>
            )}
            {success && (
                <div style={{ ...box, background: '#ecfdf3', borderColor: '#86efac', color: '#166534', marginBottom: '0.8rem' }}>
                    ✓ {success}
                </div>
            )}
            {resetFallbackUrl && (
                <div style={{ ...box, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e3a8a', marginBottom: '0.8rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>Fallback reset link (email not delivered)</div>
                    <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>Send this link to the user directly so they can reset their password now. It expires in 1 hour.</div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input readOnly value={resetFallbackUrl} onFocus={e => e.target.select()} style={{ flex: 1, minWidth: 0, fontSize: '0.78rem', padding: '0.35rem 0.5rem', border: '1px solid #bfdbfe', borderRadius: 6, fontFamily: 'monospace', background: '#fff' }} />
                        <button onClick={() => navigator.clipboard?.writeText(resetFallbackUrl)} style={{ flexShrink: 0, fontSize: '0.78rem', fontWeight: 600, padding: '0.35rem 0.7rem', border: '1px solid #3b82f6', borderRadius: 6, background: '#fff', color: '#1e3a8a', cursor: 'pointer' }}>Copy</button>
                    </div>
                </div>
            )}

            {newUserCredentials && (
                <div style={{ ...box, background: '#fffbeb', borderColor: '#fcd34d', color: '#78350f', marginBottom: '0.8rem' }}>
                    Temporary credentials generated for {newUserCredentials.email}. Temporary password: <strong>{newUserCredentials.temporaryPassword}</strong>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', borderBottom: '2px solid #e8e8e8' }}>
                {[
                    { key: 'users', label: 'Users' },
                    { key: 'worklog', label: 'Work Log' },
                    { key: 'requests', label: pendingCount > 0 ? `Requests (${pendingCount})` : 'Requests' },
                    ...(role === 'super_admin' ? [{ key: 'ownership', label: 'Ownership' }] : []),
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => { setTab(key); if (key !== 'users') closeUserDetail(); }}
                        style={{
                            background: 'none',
                            border: 'none',
                            borderBottom: tab === key ? `3px solid ${NAVY}` : '3px solid transparent',
                            padding: '0.6rem 1.1rem',
                            fontWeight: tab === key ? 900 : 600,
                            color: tab === key ? NAVY : '#667085',
                            cursor: 'pointer',
                            fontFamily: 'Raleway, sans-serif',
                            fontSize: '0.95rem',
                            marginBottom: '-2px',
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* ── USERS TAB: list ── */}
            {tab === 'users' && !selectedUserId && (
                <div style={box}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
                        <div style={{ fontWeight: 800, color: NAVY }}>
                            All Users {!loadingUsers && `(${filteredUsers.length})`}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                style={{ ...inputBase, width: '100%', maxWidth: 220, marginBottom: 0 }}
                                placeholder="Search name, email, status..."
                                value={queryText}
                                onChange={(e) => setQueryText(e.target.value)}
                            />
                            <select
                                style={{ ...inputBase, width: 'auto', marginBottom: 0 }}
                                value={planFilter}
                                onChange={(e) => setPlanFilter(e.target.value)}
                            >
                                <option value="">All plans</option>
                                {plans.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                            {role === 'super_admin' && (
                                <button
                                    onClick={openAddUserModal}
                                    style={{ ...btnBase, background: NAVY, color: '#fff' }}
                                >
                                    + Add User
                                </button>
                            )}
                        </div>
                    </div>
                    {loadingUsers && <div style={{ color: '#667085', padding: '1rem 0' }}>Loading users...</div>}
                    {!loadingUsers && (
                        <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: 520 }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={thStyle}>Name / Email</th>
                                        <th style={thStyle}>Plan</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>User Type</th>
                                        <th style={thStyle}>Terms</th>
                                        <th style={thStyle}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length === 0 && (
                                        <tr>
                                            <td colSpan={6} style={{ padding: '1.5rem', color: '#64748b', textAlign: 'center' }}>
                                                No users found.
                                            </td>
                                        </tr>
                                    )}
                                    {filteredUsers.map((u, i) => (
                                        <tr
                                            key={u.id}
                                            style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc', cursor: 'pointer' }}
                                            onClick={() => openUserDetail(u.id)}
                                        >
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 800, color: '#111827' }}>{u.name || 'Unnamed'}</div>
                                                <div style={{ fontSize: '0.76rem', color: '#6b7280' }}>{u.email}</div>
                                            </td>
                                            <td style={tdStyle}>{planPill(u.effectivePlan || u.plan)}</td>
                                            <td style={tdStyle}>{statusPill(u.status)}</td>
                                            <td style={tdStyle}>{userTypePill(u.userType)}</td>
                                            <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#334155', whiteSpace: 'nowrap' }}>
                                                {programmeTerms(u)}
                                            </td>
                                            <td style={{ ...tdStyle, color: NAVY, fontWeight: 700, fontSize: '0.8rem' }}>View →</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {showAddUserModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(2, 6, 23, 0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                    <div style={{ ...box, width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                            <div style={{ fontSize: '1.05rem', fontWeight: 900, color: NAVY }}>Add User</div>
                            <button onClick={closeAddUserModal} style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}>Close</button>
                        </div>

                        <form onSubmit={submitAddUser} className="aidey-admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <label style={labelStyle}>Full name
                                <input style={inputBase} value={addUserForm.name} onChange={(e) => setAddUserForm((p) => ({ ...p, name: e.target.value }))} placeholder="Jane Doe" required />
                            </label>
                            <label style={labelStyle}>Email
                                <input type="email" style={inputBase} value={addUserForm.email} onChange={(e) => setAddUserForm((p) => ({ ...p, email: e.target.value }))} placeholder="jane@example.com" required />
                            </label>
                            <label style={labelStyle}>Role
                                <select style={inputBase} value={addUserForm.role} onChange={(e) => setAddUserForm((p) => ({ ...p, role: e.target.value }))}>
                                    {createUserRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </label>
                            <label style={labelStyle}>Access level (plan)
                                <select style={inputBase} value={addUserForm.plan} onChange={(e) => setAddUserForm((p) => ({ ...p, plan: e.target.value }))}>
                                    {editablePlans.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </label>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div style={labelStyle}>Personal plan</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                    {PERSONAL_PLAN_PILLS.map(({ label, value }) => {
                                        const sel = addUserForm.personalTier === value;
                                        return (
                                            <button key={value} type="button"
                                                onClick={() => setAddUserForm((p) => ({ ...p, personalTier: value }))}
                                                style={{ border: '1.5px solid #f59e0b', borderRadius: 999, padding: '0.3rem 0.85rem', fontWeight: 700, fontFamily: 'Raleway, sans-serif', fontSize: '0.82rem', cursor: 'pointer', background: sel ? '#f59e0b' : 'transparent', color: '#92400e', transition: 'background 0.1s' }}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <label style={labelStyle}>Status
                                <select style={inputBase} value={addUserForm.status} onChange={(e) => setAddUserForm((p) => ({ ...p, status: e.target.value }))}>
                                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </label>
                            <label style={labelStyle}>Temporary password (optional)
                                <input style={inputBase} value={addUserForm.password} onChange={(e) => setAddUserForm((p) => ({ ...p, password: e.target.value }))} placeholder="Leave blank to auto-generate" />
                            </label>
                            <label style={labelStyle}>Extend trial by days
                                <input type="number" min="0" max="365" style={inputBase} value={addUserForm.trialDays} onChange={(e) => setAddUserForm((p) => ({ ...p, trialDays: e.target.value }))} placeholder="e.g. 14" />
                            </label>
                            <label style={labelStyle}>Programme
                                <select style={inputBase} value={addUserForm.programmeType} onChange={(e) => setAddUserForm((p) => ({ ...p, programmeType: e.target.value }))}>
                                    <option value="none">None</option>
                                    {grantableProgrammes.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </label>
                            {(addUserForm.programmeType === 'comp_fixed' || addUserForm.programmeType === 'trial') && (
                                <label style={labelStyle}>Programme ends
                                    <input type="date" style={inputBase} value={addUserForm.programmeEndsAt} onChange={(e) => setAddUserForm((p) => ({ ...p, programmeEndsAt: e.target.value }))} />
                                </label>
                            )}
                            {addUserForm.programmeType === 'discount' && (
                                <label style={labelStyle}>Discount %
                                    <input type="number" min="0" max="100" style={inputBase} value={addUserForm.programmeDiscountPercent} onChange={(e) => setAddUserForm((p) => ({ ...p, programmeDiscountPercent: e.target.value }))} placeholder="e.g. 25" />
                                </label>
                            )}
                            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>Reason / note
                                <input style={inputBase} value={addUserForm.reason} onChange={(e) => setAddUserForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Why this account is being created" />
                            </label>
                            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.2rem' }}>
                                <button type="button" onClick={closeAddUserModal} style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}>Cancel</button>
                                <button type="submit" disabled={addUserLoading} style={{ ...btnBase, background: NAVY, color: '#fff' }}>{addUserLoading ? 'Creating...' : 'Create User'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── USERS TAB: detail ── */}
            {tab === 'users' && selectedUserId && (
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                        <button
                            onClick={closeUserDetail}
                            style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}
                        >
                            ← Back to Users
                        </button>
                    </div>

                    {/* User identity card */}
                    {selectedUser && (
                        <div style={box}>
                            <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#111827' }}>
                                {selectedUser.name || 'Unnamed user'}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '0.86rem', marginBottom: '0.55rem' }}>{selectedUser.email}</div>
                            <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                {rolePill(selectedUser.role)}
                                {statusPill(selectedUser.status)}
                                {planPill(selectedUser.effectivePlan || selectedUser.plan)}
                                {userTypePill(selectedUser.userType)}
                            </div>
                            {selectedUser.trialEndsAt && (
                                <div style={{ marginTop: '0.4rem', color: '#64748b', fontSize: '0.78rem' }}>
                                    Trial ends: {friendlyDate(selectedUser.trialEndsAt)}
                                </div>
                            )}
                            <div style={{ marginTop: '0.65rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={() => sendResetLink(selectedUser.id)}
                                    disabled={saving}
                                    style={{ ...btnBase, background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}
                                >
                                    {saving ? 'Sending...' : 'Send Forgot Password Email'}
                                </button>
                                {(role === 'super_admin' || role === 'cs_manager') && (
                                    <button
                                        type="button"
                                        onClick={() => removeUser(selectedUser.id)}
                                        disabled={saving}
                                        style={{ ...btnBase, background: DANGER, color: '#fff' }}
                                    >
                                        Remove User
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Account change form */}
                    {selectedUser && (
                        <form onSubmit={submitAccountChange} style={box}>
                            <div style={{ fontWeight: 800, color: NAVY, marginBottom: '0.7rem' }}>Account Change</div>
                            <div className="aidey-admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                {role === 'super_admin' && (
                                    <label style={labelStyle}>Role
                                        <select style={inputBase} value={accountForm.role} onChange={(e) => setAccountForm((p) => ({ ...p, role: e.target.value }))}>
                                            {createUserRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    </label>
                                )}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <div style={labelStyle}>Business plan</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                        {BUSINESS_PLAN_PILLS.map(({ label, value }) => {
                                            const sel = accountForm.plan === value;
                                            return (
                                                <button key={value} type="button"
                                                    onClick={() => setAccountForm((p) => ({ ...p, plan: value }))}
                                                    style={{ border: `1.5px solid ${NAVY}`, borderRadius: 999, padding: '0.3rem 0.85rem', fontWeight: 700, fontFamily: 'Raleway, sans-serif', fontSize: '0.82rem', cursor: 'pointer', background: sel ? NAVY : 'transparent', color: sel ? '#fff' : NAVY, transition: 'background 0.1s, color 0.1s' }}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {(role === 'super_admin' || role === 'cs_manager') && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <div style={labelStyle}>Personal plan</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                            {PERSONAL_PLAN_PILLS.map(({ label, value }) => {
                                                const sel = accountForm.personalTier === value;
                                                return (
                                                    <button key={value} type="button"
                                                        onClick={() => setAccountForm((p) => ({ ...p, personalTier: value }))}
                                                        style={{ border: '1.5px solid #f59e0b', borderRadius: 999, padding: '0.3rem 0.85rem', fontWeight: 700, fontFamily: 'Raleway, sans-serif', fontSize: '0.82rem', cursor: 'pointer', background: sel ? '#f59e0b' : 'transparent', color: '#92400e', transition: 'background 0.1s' }}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                <label style={labelStyle}>Status
                                    <select style={inputBase} value={accountForm.status} onChange={(e) => setAccountForm((p) => ({ ...p, status: e.target.value }))}>
                                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </label>
                                <label style={labelStyle}>Extend trial (days)
                                    <input type="number" min="0" max="365" style={inputBase} value={accountForm.trialDays} onChange={(e) => setAccountForm((p) => ({ ...p, trialDays: e.target.value }))} placeholder="e.g. 14" />
                                </label>
                                <label style={labelStyle}>Programme
                                    <select style={inputBase} value={accountForm.programmeType} onChange={(e) => setAccountForm((p) => ({ ...p, programmeType: e.target.value }))}>
                                        <option value="none">None</option>
                                        {/* keep the loaded type selectable even if this admin can't grant it, so it isn't silently dropped */}
                                        {[...new Set([...(accountForm.programmeType && accountForm.programmeType !== 'none' ? [accountForm.programmeType] : []), ...grantableProgrammes])].map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </label>
                                {(accountForm.programmeType === 'comp_fixed' || accountForm.programmeType === 'trial') && (
                                    <label style={labelStyle}>Programme ends
                                        <input type="date" style={inputBase} value={accountForm.programmeEndsAt} onChange={(e) => setAccountForm((p) => ({ ...p, programmeEndsAt: e.target.value }))} />
                                    </label>
                                )}
                                {accountForm.programmeType === 'discount' && (
                                    <label style={labelStyle}>Discount %
                                        <input type="number" min="0" max="100" style={inputBase} value={accountForm.programmeDiscountPercent} onChange={(e) => setAccountForm((p) => ({ ...p, programmeDiscountPercent: e.target.value }))} placeholder="e.g. 25" />
                                    </label>
                                )}
                                <label style={labelStyle}>Reason
                                    <input style={inputBase} value={accountForm.reason} onChange={(e) => setAccountForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Why is this change being made?" />
                                </label>
                            </div>
                            <div style={{ marginTop: '0.85rem', display: 'flex', justifyContent: 'flex-end' }}>
                                <button type="submit" disabled={saving} style={{ ...btnBase, background: NAVY, color: '#fff' }}>
                                    {saving ? 'Saving...' : 'Save & Log Change'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Audit history */}
                    <div style={box}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                            <div style={{ fontWeight: 800, color: NAVY }}>
                                Audit History {!loadingUserLogs && `(${userDetailLogs.length} entries)`}
                            </div>
                            <button
                                onClick={exportUserLogsCsv}
                                disabled={userDetailLogs.length === 0}
                                style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', opacity: userDetailLogs.length === 0 ? 0.5 : 1, cursor: userDetailLogs.length === 0 ? 'not-allowed' : 'pointer' }}
                            >
                                Export CSV
                            </button>
                        </div>
                        {loadingUserLogs && <div style={{ color: '#667085' }}>Loading audit history...</div>}
                        {!loadingUserLogs && userDetailLogs.length === 0 && (
                            <div style={{ color: '#64748b' }}>No audit history for this user.</div>
                        )}
                        {!loadingUserLogs && userDetailLogs.length > 0 && (
                            <div style={{ maxHeight: 500, overflowY: 'auto', display: 'grid', gap: '0.5rem' }}>
                                {userDetailLogs.map((log) => <LogEntry key={log.id} log={log} />)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── WORK LOG TAB ── */}
            {tab === 'worklog' && (
                <div style={box}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.85rem' }}>
                        <div style={{ fontWeight: 800, color: NAVY }}>Recent Account Changes</div>
                        <button
                            onClick={loadGlobalLogs}
                            disabled={loadingGlobalLogs}
                            style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}
                        >
                            {loadingGlobalLogs ? 'Loading...' : 'Refresh'}
                        </button>
                    </div>
                    {loadingGlobalLogs && <div style={{ color: '#667085' }}>Loading work log...</div>}
                    {!loadingGlobalLogs && globalLogs.length === 0 && (
                        <div style={{ color: '#64748b' }}>No activity logged yet.</div>
                    )}
                    {!loadingGlobalLogs && globalLogs.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={thStyle}>Timestamp</th>
                                        <th style={thStyle}>Action</th>
                                        <th style={thStyle}>Target User</th>
                                        <th style={thStyle}>Actor</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {globalLogs.map((log, i) => (
                                        <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                            <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                {friendlyDate(log.createdAt)}
                                            </td>
                                            <td style={{ ...tdStyle, fontWeight: 700, color: '#0f172a' }}>
                                                {actionLabel(log.action)}
                                            </td>
                                            <td style={tdStyle}>
                                                {log.targetUser ? (
                                                    <button
                                                        onClick={() => openUserDetail(log.targetUser.id)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                                                    >
                                                        <div style={{ fontWeight: 700, color: NAVY, fontSize: '0.84rem' }}>
                                                            {log.targetUser.name || log.targetUser.email}
                                                        </div>
                                                        {log.targetUser.name && (
                                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{log.targetUser.email}</div>
                                                        )}
                                                    </button>
                                                ) : '—'}
                                            </td>
                                            <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.82rem' }}>
                                                {log.actorUser?.name || log.actorUser?.email || 'system'}
                                            </td>
                                            <td style={tdStyle}>{logStatusPill(log.status)}</td>
                                            <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.8rem', maxWidth: 220 }}>
                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {log.reason || '—'}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── OWNERSHIP TAB (SEAT-5, super_admin break-glass) ── */}
            {tab === 'ownership' && role === 'super_admin' && (
                <OwnershipTransferPanel />
            )}

            {/* ── REQUESTS TAB ── */}
            {tab === 'requests' && (
                <div style={box}>
                    <div style={{ fontWeight: 800, color: NAVY, marginBottom: '0.85rem' }}>
                        Pending Trial Extension Requests {pendingCount > 0 && `(${pendingCount})`}
                    </div>
                    {loadingRequests && <div style={{ color: '#667085' }}>Loading requests...</div>}
                    {!loadingRequests && requests.length === 0 && (
                        <div style={{ color: '#64748b' }}>No pending requests.</div>
                    )}
                    {!loadingRequests && requests.length > 0 && (
                        <div style={{ display: 'grid', gap: '0.8rem' }}>
                            {requests.map((r) => {
                                const rv = reviewState[r.id];
                                return (
                                    <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.9rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.45rem' }}>
                                            <div>
                                                <div style={{ fontWeight: 900, color: '#0f172a', fontSize: '0.95rem' }}>
                                                    {r.targetUser?.name || r.targetUser?.email || 'Unknown User'}
                                                </div>
                                                <div style={{ color: '#64748b', fontSize: '0.78rem' }}>{r.targetUser?.email}</div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                {planPill(r.targetUser?.plan)}
                                                {statusPill(r.targetUser?.status)}
                                            </div>
                                        </div>
                                        <div style={{ color: '#475569', fontSize: '0.83rem', marginBottom: '0.2rem' }}>
                                            Requested by <strong>{r.actorUser?.name || r.actorUser?.email}</strong>
                                            {r.actorUser?.role && ` (${r.actorUser.role.replace('_', ' ')})`}
                                            {' · '}<strong>{r.metadata?.requestedDays || '?'} day(s)</strong>
                                        </div>
                                        {r.reason && (
                                            <div style={{ color: '#334155', fontSize: '0.82rem', marginBottom: '0.25rem' }}>{r.reason}</div>
                                        )}
                                        <div style={{ color: '#94a3b8', fontSize: '0.76rem', marginBottom: '0.7rem' }}>
                                            {friendlyDate(r.createdAt)}
                                        </div>

                                        {!rv && (
                                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                <button onClick={() => startReview(r.id, 'approve')} disabled={saving} style={{ ...btnBase, background: '#166534', color: '#fff' }}>
                                                    Approve
                                                </button>
                                                <button onClick={() => startReview(r.id, 'reject')} disabled={saving} style={{ ...btnBase, background: '#991b1b', color: '#fff' }}>
                                                    Reject
                                                </button>
                                                <button onClick={() => openUserDetail(r.targetUser?.id)} style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}>
                                                    View User
                                                </button>
                                            </div>
                                        )}

                                        {rv && (
                                            <div style={{
                                                background: rv.mode === 'approve' ? '#f0fdf4' : '#fff1f2',
                                                border: `1px solid ${rv.mode === 'approve' ? '#86efac' : '#fecdd3'}`,
                                                borderRadius: 10,
                                                padding: '0.8rem',
                                                marginTop: '0.3rem',
                                            }}>
                                                <div style={{ fontWeight: 800, color: rv.mode === 'approve' ? '#166534' : '#991b1b', marginBottom: '0.55rem', fontSize: '0.88rem' }}>
                                                    {rv.mode === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                                                </div>
                                                <div className="aidey-admin-form-grid" style={{ display: 'grid', gridTemplateColumns: rv.mode === 'approve' ? '1fr 2fr' : '1fr', gap: '0.5rem', marginBottom: '0.65rem' }}>
                                                    {rv.mode === 'approve' && (
                                                        <label style={labelStyle}>Approved days
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max="365"
                                                                style={inputBase}
                                                                value={rv.approvedDays}
                                                                placeholder={`Requested: ${r.metadata?.requestedDays || '?'}`}
                                                                onChange={(e) => setReviewState((prev) => ({
                                                                    ...prev, [r.id]: { ...prev[r.id], approvedDays: e.target.value },
                                                                }))}
                                                            />
                                                        </label>
                                                    )}
                                                    <label style={labelStyle}>Reason (optional)
                                                        <input
                                                            style={inputBase}
                                                            value={rv.reason}
                                                            placeholder={rv.mode === 'approve' ? 'Approval note...' : 'Reason for rejection...'}
                                                            onChange={(e) => setReviewState((prev) => ({
                                                                ...prev, [r.id]: { ...prev[r.id], reason: e.target.value },
                                                            }))}
                                                        />
                                                    </label>
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.45rem' }}>
                                                    <button
                                                        onClick={() => confirmReview(r.id)}
                                                        disabled={saving}
                                                        style={{ ...btnBase, background: rv.mode === 'approve' ? '#166534' : '#991b1b', color: '#fff' }}
                                                    >
                                                        {saving ? '...' : rv.mode === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                                                    </button>
                                                    <button
                                                        onClick={() => cancelReview(r.id)}
                                                        style={{ ...btnBase, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <style>{`
        @media (max-width: 680px) {
          .aidey-admin-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
        </div>
    );
}

function LogEntry({ log }) {
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.65rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.88rem' }}>{actionLabel(log.action)}</div>
                <div style={{ fontSize: '0.76rem', color: '#64748b' }}>{friendlyDate(log.createdAt)}</div>
            </div>
            <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{
                    fontSize: '0.72rem', padding: '0.15rem 0.45rem', borderRadius: 999, fontWeight: 800,
                    background: log.status === 'pending' ? '#fff7ed' : log.status === 'completed' ? '#ecfdf5' : '#fee2e2',
                    color: log.status === 'pending' ? '#9a3412' : log.status === 'completed' ? '#166534' : '#991b1b',
                }}>{log.status}</span>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    by {log.actorUser?.name || log.actorUser?.email || 'system'}
                </span>
                {log.approvedByUser && (
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                        · approved by {log.approvedByUser.name || log.approvedByUser.email}
                    </span>
                )}
            </div>
            {log.reason && (
                <div style={{ marginTop: '0.3rem', fontSize: '0.8rem', color: '#334155' }}>{log.reason}</div>
            )}
        </div>
    );
}
